import { Component, OnInit, computed, input, signal, viewChild } from '@angular/core';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { RegionManager } from '@scenario/models/region-manager.model';
import { PsfUser } from '@scenario/models/psf-user.model';
import { Source } from '@scenario/models/source.model';
import { dispatch, select } from '@store/injectables';
import { createRegionManager, selectRegionManagerViewModel } from '@store/scenario/region-manager/region-manager-slice';
import { retrieveMarketAreas, selectMarketAreasViewModel } from '@store/scenario/market-areas/market-areas-slice';
import {
  ButtonComponent,
  ModalActionsDirective,
  ModalComponent,
  ModalTitleDirective,
} from '@totalenergiescode/tds-angular';
@Component({
  selector: 'app-region-manager-form-modal',
  templateUrl: './region-manager-form-modal.component.html',
  styleUrl: './region-manager-form-modal.component.scss',
  imports: [
    ButtonComponent,
    ModalActionsDirective,
    ModalComponent,
    ModalTitleDirective,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
  ],
})
export class RegionManagerFormModalComponent implements OnInit {
  private readonly dispatch = dispatch();
  public readonly modal = viewChild.required(ModalComponent);
  public readonly source = input.required<Source>();
  public readonly users = input.required<PsfUser[]>();
  public readonly marketAreasViewModel = select(selectMarketAreasViewModel);
  public readonly regionManagerViewModel = select(selectRegionManagerViewModel);
  public readonly selectedUserId = signal<number | undefined>(undefined);
  public readonly selectedMarketAreaId = signal<number | undefined>(undefined);
  public readonly firstName = signal('');
  public readonly lastName = signal('');
  public readonly submitted = signal(false);
  public readonly userError = computed(() => this.submitted() && this.selectedUserId() === undefined);
  public readonly duplicateError = computed(() => {
    const marketAreaId = this.selectedMarketAreaId();
    return this.submitted() && marketAreaId !== undefined && this.regionManagerViewModel().regionManagers.some(
      existing => !existing.endDate && !existing.removedByEmail
        && existing.source?.id === this.source().id
        && existing.marketArea?.id === marketAreaId,
    );
  });
  ngOnInit(): void {
    this.dispatch(retrieveMarketAreas());
  }
  userLabel(user: PsfUser): string {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return name ? `${user.email} — ${name}` : user.email;
  }
  onUserChange(userId: number | undefined): void {
    this.selectedUserId.set(userId);
  }
  onMarketAreaChange(marketAreaId: number | undefined): void {
    this.selectedMarketAreaId.set(marketAreaId);
  }
  onFirstNameChange(event: Event): void {
    this.firstName.set((event.target as HTMLInputElement).value);
  }
  onLastNameChange(event: Event): void {
    this.lastName.set((event.target as HTMLInputElement).value);
  }
  save(): void {
    this.submitted.set(true);
    if (this.userError() || this.duplicateError()) {
      return;
    }
    const user = this.users().find(candidate => candidate.id === this.selectedUserId());
    if (!user) {
      return;
    }
    const marketArea = this.marketAreasViewModel().marketAreas.find(
      candidate => candidate.id === this.selectedMarketAreaId(),
    );
    const regionManager: RegionManager = {
      userId: user.id,
      source: this.source(),
      marketArea,
      firstName: this.firstName().trim() || undefined,
      lastName: this.lastName().trim() || undefined,
      email: user.email,
    };
    this.dispatch(createRegionManager(regionManager));
    this.modal().closeModal('saved');
  }
  close(): void {
    this.modal().closeModal('cancel');
  }
}
